import type { PushSdkInternals } from '../push-sdk-internal-impl'
import type { PushSdkConsentState } from '../types/push-sdk-config.ts'
import { SubscriberStatus } from '../enums/subscriber-status'
import type { SubscriptionToken } from '@core/types/subscription-token.ts'
import type { DomainECommConfig } from '@core/types/domain-config.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger'
import { TrackedEventAction } from '@core/events/tracked-event-action'
import { TrackedEventUtils } from '@core/events/tracked-event'
import { Globals } from '../globals.ts'
import { getTrackableKey } from '../enums/e-comm-item-type.ts'
import {
    type ECommActivity,
    type ECommItem,
    type ECommItemWithRating,
    type ECommItemWithoutQuantity,
    type ECommItemWithQuantity,
    type ECommPurchaseActivity,
    eCommItemOf,
    eCommItemWithRatingOf,
    eCommRatingOf,
} from '../types/ecomm.ts'

/** @public */
export type UserProfile = {
    /**
     * Returns the user's anonymous Push ID for event tracking
     * and debugging purposes.
     */
    getAnonymousId(): Promise<string>

    /**
     * Returns the user's current subscription data or `null`
     * if no active subscription exists.
     */
    getToken(): Promise<SubscriptionToken | null>

    /**
     * Returns the user's current external identifier or `null`
     * if an identifier has not been previously set.
     */
    getExternalId(): Promise<string | null>

    /**
     * Updates the user's current external identifier.
     */
    setExternalId(id: string): Promise<void>

    /**
     * Unregisters the user's current external identifier.
     */
    unsetExternalId(): Promise<void>

    /**
     * Returns the user's current {@link SubscriberStatus}.
     */
    getSubscriberStatus(): Promise<SubscriberStatus>

    /**
     * Returns whether this browser has requested deletion via
     * [`PushlySDK.UserProfile.requestUserDeletion()`]{@link requestUserDeletion}
     * or the `pushly_udr` URL parameter, and has not opted back in since. An opt-out is not a
     * deletion and does not count.
     */
    getIsDeleted(): Promise<boolean>

    /**
     * Requests deletion for this browser: stops notifications and all tracking, removes the push
     * subscription, reports the request and gives the browser a new anonymous id. Pushly then erases
     * the data that belongs only to this browser. A profile that is also linked to an account
     * (an external id) or to other devices is deactivated instead, and its erasure is requested
     * through the Privacy API.
     */
    requestUserDeletion(): void

    /**
     * Withdraws this browser's consent: removes the push subscription, stops all tracking and
     * prompts, and reports the withdrawal. It applies to this browser only and lasts until the
     * visitor opts back in or clears the site's data. It is not a deletion request; use `requestUserDeletion()` for that.
     *
     * Called before the SDK has loaded, it is queued and the promise resolves at once; the
     * withdrawal itself happens as the SDK loads.
     */
    optOut(): Promise<void>

    /**
     * Lets a visitor who opted out or requested deletion in this browser be subscribed and tracked
     * again. It takes effect on the next page load, so reload the page (or wait for the next
     * navigation) after calling it. After a deletion the browser returns as a new visitor; nothing
     * that was erased comes back.
     *
     * Called before the SDK has loaded, it is queued and the promise resolves at once; the opt-in
     * itself happens as the SDK loads.
     */
    optIn(): Promise<void>

    /**
     * Reports the visitor's consent to push notifications and tracking when your consent platform
     * answers or changes, for example when the visitor responds to your banner.
     *
     * - `'granted'` on a page that loaded with `consent: 'pending'` starts the SDK on this page. On a
     *   page that already loaded, it undoes an opt-out caused by `denied` from the next page load on.
     *   It never undoes `opt_out` or a deletion.
     * - `'denied'` withdraws as `consent: 'denied'` does: a subscribed browser is unsubscribed and
     *   reported as opted out, and this page stops tracking. On a page still waiting at `pending`,
     *   the SDK never starts.
     * - `'pending'` changes nothing once consent has been decided.
     *
     * Called before `load`, the state is used by `load` in place of its `consent` setting.
     */
    setConsent(state: PushSdkConsentState): Promise<void>

    /**
     * Updates a single key/value on the user's profile.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.set('interests', ['politics', 'news'])
     * ```
     */
    set(key: string, value: any): void

    /**
     * Updates multiple key/values at once on the user's profile.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.set({
     *   is_paying_subscriber: true,
     *   interests: ['politics', 'news']
     * })
     * ```
     */
    set(data: { [key: string]: any }): void

    /**
     * Adds values to the specified user's profile key.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.append('interests', ['sports'])
     * ```
     */
    append(key: string, values: any[]): void

    /**
     * Removes values for the specified user's profile key.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.append('interests', ['fashion', 'news'])
     * ```
     */
    remove(key: string, values: any[]): void

    saveItem(item: ECommItemWithoutQuantity): Promise<void>

    viewItem(item: ECommItemWithoutQuantity): Promise<void>

    completeItem(item: ECommItemWithoutQuantity): Promise<void>

    unsaveItem(item: ECommItemWithoutQuantity): Promise<void>

    uncompleteItem(item: ECommItemWithoutQuantity): Promise<void>

    unrateItem(item: ECommItemWithoutQuantity): Promise<void>

    rateItem(item: ECommItemWithRating): Promise<void>

    addToCart(items: ECommItemWithQuantity[]): Promise<void>

    updateCart(items: ECommItemWithQuantity[]): Promise<void>

    clearCart(): Promise<void>

    purchase(): Promise<void>

    purchase(items: ECommItem[]): Promise<void>

    purchase(items?: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void>

    /**
     * Tracks the user's flow through your site via URI.
     *
     * This information can then be used in segmentation to create cohorts of subscribers
     * that have visited specific URLs based on number of visits and recency.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.trackActivity('https://www.my-site.com/article-1')
     * ```
     */
    trackActivity(name: string): void

    /**
     * Tracks the user's flow through your site via URI with associated metadata/tags.
     *
     * This information can then be used in segmentation to create cohorts of subscribers
     * that have visited specific URLs with tags based on number of visits and recency.
     *
     * @example
     * ```typescript
     * PushlySDK.UserProfile.trackActivity('https://www.my-site.com/article-1', ['sports'])
     * ```
     */
    trackActivity(name: string, tags: string[]): void
}

/** @internal */
export type UserProfileEComm = UserProfile & {
    saveItems(items: ECommItemWithoutQuantity[]): Promise<void>
    viewItems(items: ECommItemWithoutQuantity[]): Promise<void>
    completeItems(items: ECommItemWithoutQuantity[]): Promise<void>
    unsaveItems(items: ECommItemWithoutQuantity[]): Promise<void>
    uncompleteItems(items: ECommItemWithoutQuantity[]): Promise<void>
    unrateItems(items: ECommItemWithoutQuantity[]): Promise<void>
    rateItems(items: ECommItemWithRating[]): Promise<void>
}

/** @internal */
export class UserProfileImpl implements UserProfileEComm {
    readonly #sdk: PushSdkInternals
    readonly #logger: Logger

    public constructor(sdk: PushSdkInternals, logger?: Logger) {
        this.#sdk = sdk
        this.#logger = logger ?? TaggedLogger._getInstance('UserProfile')
    }

    public async getAnonymousId() {
        return this.#sdk.userStore._getAnonymousId()
    }

    public async getToken(): Promise<SubscriptionToken | null> {
        if (!this.#sdk.notificationPermissionService) {
            return null
        }

        const perms = await this.#sdk.notificationPermissionService._getCurrentPermissions()
        return perms.token
    }

    public async setExternalId(id: string): Promise<void> {
        if (!this.#sdk.isLoaded) {
            this.#sdk._registerOnLoadedCallback(() => this.setExternalId(id))
            return
        }

        // An opted-out browser keeps no customer identifier on the device.
        if (await this.#sdk.userStore._getIsOptedOut()) {
            this.#logger.debug('External id not stored: the user is opted out.')
            return
        }

        const currXid = await this.#sdk.userStore._getExternalId()
        if (!currXid || id !== currXid) {
            await this.#sdk.userStore._setExternalId(id)

            const event = await TrackedEventUtils._getProfileExternalIdEvent(id)
            this.#sdk.eventManager._track(event)
        }
    }

    public async getExternalId(): Promise<string | null> {
        return this.#sdk.userStore._getExternalId()
    }

    public async unsetExternalId() {
        const unsetId = async () => {
            const externalId = this.getExternalId()
            if (externalId !== null) {
                await this.#sdk.userStore._removeExternalId()

                const event = await TrackedEventUtils._build(TrackedEventAction.DEREGISTER_EXTERNAL_ID)
                this.#sdk.eventManager._track(event)
            }
        }

        unsetId()
    }

    public async getSubscriberStatus(): Promise<SubscriberStatus> {
        // The stored status is not a reliable answer once tracking halts: the halt clears it to
        // not-determined, and a sibling host's stored `subscribed` wins arbitration over that.
        if (await this.#sdk.userStore._getIsTrackingHalted()) {
            return SubscriberStatus.NOT_DETERMINED
        }

        return this.#sdk.userStore._getSubscriberStatus()
    }

    public async getIsDeleted(): Promise<boolean> {
        return this.#sdk.userStore._getIsInDeletedState()
    }

    public requestUserDeletion(): void {
        // Queued behind on-loaded work, so an external id given in the same breath is applied
        // first and reported with the request. The callback returns the run itself, so the load
        // waits for it and starts nothing for a visitor it has just deleted.
        if (!this.#sdk.isLoaded) {
            this.#sdk._registerOnLoadedCallback(() =>
                this.#sdk._executeWhenConfigured(() => this.#sdk._handleUserDeletionRequest()),
            )
            return
        }

        this.#sdk._executeWhenConfigured(() => this.#sdk._handleUserDeletionRequest())
    }

    public async optOut(): Promise<void> {
        // Before load this only queues the opt-out and resolves straight away; the load runs it.
        if (!this.#sdk.isLoaded) {
            this.#sdk._registerOnLoadedCallback(() =>
                this.#sdk._executeWhenConfigured(() => this.#sdk._handleOptOutRequest()),
            )
            return
        }

        await this.#sdk._executeWhenConfigured(() => this.#sdk._handleOptOutRequest())
    }

    public async optIn(): Promise<void> {
        // Before load this only queues the opt-in and resolves straight away; the load runs it.
        if (!this.#sdk.isLoaded) {
            this.#sdk._registerOnLoadedCallback(() =>
                this.#sdk._executeWhenConfigured(() => this.#sdk._handleOptInRequest()),
            )
            return
        }

        await this.#sdk._executeWhenConfigured(() => this.#sdk._handleOptInRequest())
    }

    public async setConsent(state: PushSdkConsentState): Promise<void> {
        // Not queued for after load like the other commands: a held load waits on this call, and a
        // state given before load has to reach the consent check, which runs before anything else.
        await this.#sdk._handleConsentUpdate(state)
    }

    public set(key: string, value: any): void
    public set(data: { [key: string]: any }): void
    public set(_keyOrData: string | { [key: string]: any }, value?: any): void {
        const profileData = typeof _keyOrData === 'string' ? { [_keyOrData]: value } : _keyOrData

        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#logger.verbose(`Setting to profile: ${profileData}`)
            this.#sdk.eventManager._track(TrackedEventAction.PROFILE, profileData)
        })
    }

    public append(key: string, values: any[]): void {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#logger.verbose(`Appending to profile ${key}: ${values}`)
            this.#sdk.eventManager._track(TrackedEventAction.PROFILE_APPEND, { [key]: values })
        })
    }

    public remove(key: string, values: any[]): void {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#logger.verbose(`Removing from profile ${key}: ${values}`)
            this.#sdk.eventManager._track(TrackedEventAction.PROFILE_REMOVE, { [key]: values })
        })
    }

    public async saveItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.saveItems([item])
    }

    public async viewItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.viewItems([item])
    }

    public async completeItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.completeItems([item])
    }

    public async unsaveItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.unsaveItems([item])
    }

    public async uncompleteItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.uncompleteItems([item])
    }

    public async unrateItem(item: ECommItemWithoutQuantity): Promise<void> {
        return this.unrateItems([item])
    }

    public async rateItem(item: ECommItemWithRating): Promise<void> {
        return this.rateItems([item])
    }

    public async saveItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'saveItems', TrackedEventAction.SAVE_ITEM, 'saved items')
    }

    public async viewItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'viewItems', TrackedEventAction.VIEW_ITEM, 'viewed items')
    }

    public async completeItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'completeItems', TrackedEventAction.COMPLETE_ITEM, 'completed items')
    }

    public async unsaveItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'unsaveItems', TrackedEventAction.UNSAVE_ITEM, 'saved items')
    }

    public async uncompleteItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'uncompleteItems', TrackedEventAction.UNCOMPLETE_ITEM, 'completed items')
    }

    public async unrateItems(items: ECommItemWithoutQuantity[]): Promise<void> {
        this._trackItemsWithoutQuantity(items, 'unrateItems', TrackedEventAction.UNRATE_ITEM, 'rated items')
    }

    public async rateItems(items: ECommItemWithRating[]): Promise<void> {
        this._trackItemsWithRating(items, 'rateItems', TrackedEventAction.RATE_ITEM, 'rated items')
    }

    public async addToCart(items: ECommItemWithQuantity[]): Promise<void> {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig('addToCart', (ecommConfig: DomainECommConfig) => {
                const trackableKey = getTrackableKey(ecommConfig.item_type)
                const data: ECommActivity = {
                    [trackableKey]: items.map((item) => eCommItemOf(item, ecommConfig.item_type)),
                }
                this.#logger.verbose(`Adding ${items.length} item${items.length > 1 ? 's' : ''} to cart`)
                this.#sdk.eventManager._track(TrackedEventAction.ADD_TO_CART, data)
            })
        })
    }

    public async updateCart(items: ECommItemWithQuantity[]): Promise<void> {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig('updateCart', (ecommConfig: DomainECommConfig) => {
                const trackableKey = getTrackableKey(ecommConfig.item_type)
                const data: ECommActivity = {
                    [trackableKey]: items.map((item) => eCommItemOf(item, ecommConfig.item_type)),
                }
                this.#logger.verbose(`Updating cart with ${items.length} item${items.length > 1 ? 's' : ''}.`)
                this.#sdk.eventManager._track(TrackedEventAction.UPDATE_CART, data)
            })
        })
    }

    public async clearCart(): Promise<void> {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig('clearCart', (ecommConfig: DomainECommConfig) => {
                const trackableKey = getTrackableKey(ecommConfig.item_type)
                const data: ECommActivity = { [trackableKey]: [] }
                this.#logger.verbose('Clearing cart')
                this.#sdk.eventManager._track(TrackedEventAction.UPDATE_CART, data)
            })
        })
    }

    public async purchase(): Promise<void>
    public async purchase(items: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void>
    public async purchase(items?: ECommItem[], purchaseId?: string, priceValue?: string): Promise<void> {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig(
                `purchase(${!items ? '' : 'items'}${purchaseId ? ':purchaseId' : ''}${
                    priceValue ? ':priceValue' : ''
                })`,
                (ecommConfig: DomainECommConfig) => {
                    const data: ECommPurchaseActivity = {}
                    const detailsOnly = items === undefined && (purchaseId !== undefined || priceValue !== undefined)
                    const trackableKey = getTrackableKey(ecommConfig.item_type)

                    if (items !== undefined) {
                        data[trackableKey] = items.map((item) => eCommItemOf(item, ecommConfig.item_type))
                    }

                    if (purchaseId) {
                        data.purchase_id = purchaseId
                    }

                    if (priceValue) {
                        data.price_value = priceValue
                    }

                    if (items === undefined && !detailsOnly) {
                        this.#logger.verbose('Tracking purchase')
                        this.#sdk.eventManager._track(TrackedEventAction.PURCHASE)
                        return
                    }

                    const sen = ['Tracking purchase']
                    if (items?.length) {
                        sen.push(`of ${items.length} item${items.length > 1 ? 's' : ''}`)
                    }
                    if (purchaseId) {
                        sen.push(`Purchase ID: ${purchaseId}`)
                    }
                    if (priceValue) {
                        sen.push(`Total value: ${priceValue}`)
                    }

                    this.#logger.verbose(sen.join(' '))
                    this.#sdk.eventManager._track(TrackedEventAction.PURCHASE, data)
                },
            )
        })
    }

    public trackActivity(name: string): void
    public trackActivity(name: string, tags: string[]): void
    public trackActivity(name: string, tags?: string[]): void {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            const data: { [key: string]: any } = { page_url: name }
            if (tags?.length) {
                data['tags'] = tags
            }

            this.#sdk.eventManager._track(TrackedEventAction.VIEW_PAGE, data)
        })
    }

    protected async _withECommConfig(caller: string, callback: (config: DomainECommConfig) => void) {
        this.#sdk._executeWhenConfigured(async () => {
            const domainConfigResult = await this.#sdk.domainConfigProvider._get()
            if (domainConfigResult.isErr()) {
                this.#logger.warn(domainConfigResult.getErr())
                this.#logger.warn(`${caller} Unable to load Domain Config`)
                return
            }

            const domainConfig = domainConfigResult.unwrap()
            const ecommConfig = domainConfig.ecomm_config
            if (!ecommConfig) {
                this.#logger.warn('E-Commerce configuration is not defined for this domain')
                return
            }

            if (getTrackableKey(ecommConfig.item_type) === 'unknown') {
                this.#logger.warn(`E-Commerce configuration has unrecognized item type: ${ecommConfig.item_type}`)
                return
            }

            if (!domainConfig.flags.includes(Globals._domainFeatIntegrationsECommFlag)) {
                this.#logger.warn('E-Commerce support is not currently enabled for this domain')
                return
            }

            callback(ecommConfig)
        })
    }

    private _trackItemsWithoutQuantity(
        items: readonly ECommItemWithoutQuantity[],
        caller: string,
        action: TrackedEventAction,
        logTarget: string,
    ): void {
        const sanitizedItems = items.map(({ id, item_type }) => ({ id, item_type }))
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig(caller, (ecommConfig: DomainECommConfig) => {
                const trackableKey = getTrackableKey(ecommConfig.item_type)
                const data: ECommActivity = {
                    [trackableKey]: sanitizedItems.map((item) => eCommItemOf(item, ecommConfig.item_type)),
                }
                this.#logger.verbose(`Adding ${items.length} item${items.length > 1 ? 's' : ''} to ${logTarget}`)
                this.#sdk.eventManager._track(action, data)
            })
        })
    }

    private _trackItemsWithRating(
        items: readonly ECommItemWithRating[],
        caller: string,
        action: TrackedEventAction,
        logTarget: string,
    ): void {
        const sanitizedItems = items.map(({ id, item_type, rating }) => ({
            id,
            item_type,
            rating: eCommRatingOf(rating),
        }))
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this._withECommConfig(caller, (ecommConfig: DomainECommConfig) => {
                const trackableKey = getTrackableKey(ecommConfig.item_type)
                const data: ECommActivity = {
                    [trackableKey]: sanitizedItems.map((item) => eCommItemWithRatingOf(item, ecommConfig.item_type)),
                }
                this.#logger.verbose(`Adding ${items.length} item${items.length > 1 ? 's' : ''} to ${logTarget}`)
                this.#sdk.eventManager._track(action, data)
            })
        })
    }
}
